import React, { useState } from 'react';

interface DiscountApprovalModalProps {
  discountAmount: number;
  discountPercentage?: number;
  subtotal: number;
  onApprove: (approverId: number, approverName: string) => void;
  onCancel: () => void;
}

const DiscountApprovalModal: React.FC<DiscountApprovalModalProps> = ({
  discountAmount,
  discountPercentage,
  subtotal,
  onApprove,
  onCancel,
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/v1/sales/approve-discount', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          username,
          password,
          discount_amount: discountAmount,
          discount_percentage: discountPercentage,
          subtotal,
        }),
      });

      const data = await response.json();

      if (data.approved) {
        // Approval berhasil
        onApprove(data.approver_id!, data.approver_name!);
      } else {
        // Approval gagal
        setError(data.message);
      }
    } catch (err) {
      setError('Gagal melakukan approval. Silakan coba lagi.');
      console.error('Error approving discount:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">
            🔐 Approval Diskon Diperlukan
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            Transaksi ini memerlukan persetujuan Admin/Owner
          </p>
        </div>

        <div className="p-6">
          {/* Discount Info */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="text-sm text-gray-600">Subtotal:</div>
            <div className="text-lg font-semibold">
              Rp {subtotal.toLocaleString()}
            </div>
            
            <div className="mt-3 pt-3 border-t border-yellow-200">
              <div className="text-sm text-gray-600">Diskon yang diminta:</div>
              <div className="text-2xl font-bold text-red-600">
                Rp {discountAmount.toLocaleString()}
                {discountPercentage && (
                  <span className="text-lg ml-2">({discountPercentage}%)</span>
                )}
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-yellow-200">
              <div className="text-sm text-gray-600">Total setelah diskon:</div>
              <div className="text-xl font-bold text-green-600">
                Rp {(subtotal - discountAmount).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Approval Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username Admin/Owner
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Masukkan username"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Masukkan password"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
                disabled={loading}
              >
                Batal
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? 'Memproses...' : 'Approve Diskon'}
              </button>
            </div>
          </form>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t rounded-b-lg">
          <p className="text-xs text-gray-600">
            ℹ️ Hanya Admin atau Owner yang dapat menyetujui diskon. 
            Pastikan kredensial yang dimasukkan benar.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DiscountApprovalModal;
